import { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { dispenseService } from '../api/dispense';
import { medicineService } from '../api/medicines';
import { stockService } from '../api/stock';
import { dispenseSchema, type DispenseFormData } from '../schemas/dispenseSchema';
import type { Medicine, BatchStock } from '../types';
import { admissionService, Admission } from '../../../lib/services.ipd';
import { ipdService } from '../../../lib/pharmacyServices';
import toast from 'react-hot-toast';

export default function Dispense() {
  const { formatCurrency } = useCurrency();
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [availableBatches, setAvailableBatches] = useState<Record<string, BatchStock[]>>({});
  const [loading, setLoading] = useState(true);
  const [activeAdmissions, setActiveAdmissions] = useState<Admission[]>([]);
  const [selectedAdmissionId, setSelectedAdmissionId] = useState<string>('');
  const [patientId, setPatientId] = useState<string>('');

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<DispenseFormData>({
    resolver: zodResolver(dispenseSchema),
    defaultValues: {
      items: [{ medicineId: '', batchNo: '', qty: 1, unitPrice: 0, mrp: 0 }],
      paymentStatus: 'unpaid'
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const watchedItems = watch('items');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const medicinesData = await medicineService.getActiveOnly();
      setMedicines(medicinesData);
    } catch (error) {
      console.error('Failed to load medicines:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBatchesForMedicine = async (medicineId: string) => {
    try {
      const batches = await stockService.getAvailableBatches(medicineId);
      setAvailableBatches(prev => ({
        ...prev,
        [medicineId]: batches
      }));
    } catch (error) {
      console.error('Failed to load batches:', error);
    }
  };

  const loadActiveAdmissionsForPatient = async (patientId: string) => {
    if (!patientId) {
      setActiveAdmissions([]);
      setSelectedAdmissionId('');
      return;
    }
    try {
      const allActive = await admissionService.getActive();
      const patientAdmissions = allActive.filter(a => a.patient_id === patientId);
      setActiveAdmissions(patientAdmissions);
      if (patientAdmissions.length === 1) {
        setSelectedAdmissionId(patientAdmissions[0].id);
        setValue('admissionId', patientAdmissions[0].id);
      } else {
        setSelectedAdmissionId('');
        setValue('admissionId', '');
      }
    } catch (error) {
      console.error('Failed to load admissions:', error);
    }
  };

  const handleMedicineChange = async (index: number, medicineId: string) => {
    setValue(`items.${index}.medicineId`, medicineId);
    setValue(`items.${index}.batchNo`, '');
    setValue(`items.${index}.unitPrice`, 0);
    setValue(`items.${index}.mrp`, 0);
    
    if (medicineId) {
      await loadBatchesForMedicine(medicineId);
    }
  };

  const handleBatchChange = (index: number, batchNo: string) => {
    const medicineId = watchedItems[index]?.medicineId;
    if (medicineId && batchNo) {
      const batch = availableBatches[medicineId]?.find(b => b.batchNo === batchNo);
      if (batch) {
        setValue(`items.${index}.batchNo`, batchNo);
        setValue(`items.${index}.unitPrice`, batch.unitPrice);
        setValue(`items.${index}.mrp`, batch.mrp || 0);
      }
    }
  };

  const onSubmit = async (data: DispenseFormData) => {
    try {
      await dispenseService.create(data);
      
      if (data.admissionId) {
        try {
          const totalAmount = data.items.reduce((sum, item) => sum + (item.qty * item.unitPrice), 0);
          await ipdService.postChargeToBilling(data.admissionId, {
            productId: data.items[0].medicineId,
            quantity: data.items.length,
            price: totalAmount,
            source: 'pharmacy',
            description: `Pharmacy dispense - ${data.items.length} item(s)`
          });
          toast.success('Medicines dispensed and charges posted to admission');
        } catch (billingError) {
          console.error('Billing integration failed:', billingError);
          toast.error('Medicines dispensed but billing post failed. Please add charges manually.');
        }
      } else {
        toast.success('Medicines dispensed successfully');
      }
      
      window.location.reload();
    } catch (error) {
      console.error('Failed to create dispense:', error);
      alert('Failed to dispense medicines. Please check stock availability.');
    }
  };

  const addItem = () => {
    append({ medicineId: '', batchNo: '', qty: 1, unitPrice: 0, mrp: 0 });
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const calculateTotal = () => {
    return watchedItems.reduce((sum, item) => sum + (item.qty * item.unitPrice), 0);
  };

  const getBatchInfo = (medicineId: string, batchNo: string) => {
    const batch = availableBatches[medicineId]?.find(b => b.batchNo === batchNo);
    return batch;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dispense Medicines</h1>
          <p className="text-sm text-neutral-600">Dispense medicines to patients</p>
        </div>
      </header>

      <div className="bg-white rounded-lg border p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Patient Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Patient ID *
              </label>
              <input
                {...register('patientId')}
                placeholder="Enter patient ID"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onChange={(e) => {
                  const value = e.target.value;
                  setPatientId(value);
                  setValue('patientId', value);
                  loadActiveAdmissionsForPatient(value);
                }}
              />
              {errors.patientId && (
                <p className="text-sm text-red-600 mt-1">{errors.patientId.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Prescription ID
              </label>
              <input
                {...register('prescriptionId')}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Doctor ID
              </label>
              <input
                {...register('doctorId')}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Admission ID (Optional - for IPD billing)
              </label>
              <select
                {...register('admissionId')}
                value={selectedAdmissionId}
                onChange={(e) => {
                  setSelectedAdmissionId(e.target.value);
                  setValue('admissionId', e.target.value);
                }}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">No admission (OPD)</option>
                {activeAdmissions.map((admission) => (
                  <option key={admission.id} value={admission.id}>
                    {admission.admission_no} - {admission.patient?.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Select admission to automatically post charges to IPD billing
              </p>
            </div>

          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Medicines</h3>
              <button
                type="button"
                onClick={addItem}
                className="inline-flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Medicine
              </button>
            </div>

            <div className="space-y-4">
              {fields.map((field, index) => {
                const medicineId = watchedItems[index]?.medicineId;
                const batchNo = watchedItems[index]?.batchNo;
                const batch = getBatchInfo(medicineId, batchNo);
                const requestedQty = watchedItems[index]?.qty || 0;
                const availableQty = batch?.quantityOnHand || 0;
                const isInsufficientStock = requestedQty > availableQty;

                return (
                  <div key={field.id} className="border border-neutral-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium">Medicine {index + 1}</h4>
                      {fields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div className="lg:col-span-2">
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Medicine *
                        </label>
                        <select
                          value={medicineId}
                          onChange={(e) => handleMedicineChange(index, e.target.value)}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select medicine</option>
                          {medicines.map((medicine) => (
                            <option key={medicine.id} value={medicine.id}>
                              {medicine.name} - {medicine.strength}
                            </option>
                          ))}
                        </select>
                        {errors.items?.[index]?.medicineId && (
                          <p className="text-sm text-red-600 mt-1">
                            {errors.items[index]?.medicineId?.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Batch *
                        </label>
                        <select
                          value={batchNo}
                          onChange={(e) => handleBatchChange(index, e.target.value)}
                          disabled={!medicineId}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-neutral-100"
                        >
                          <option value="">Select batch</option>
                          {availableBatches[medicineId]?.map((batch) => (
                            <option key={batch.id} value={batch.batchNo}>
                              {batch.batchNo} (Qty: {batch.quantityOnHand}, Exp: {new Date(batch.expiryDate).toLocaleDateString()})
                            </option>
                          ))}
                        </select>
                        {errors.items?.[index]?.batchNo && (
                          <p className="text-sm text-red-600 mt-1">
                            {errors.items[index]?.batchNo?.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Quantity *
                        </label>
                        <input
                          type="number"
                          min="1"
                          max={availableQty}
                          {...register(`items.${index}.qty`, { valueAsNumber: true })}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            isInsufficientStock ? 'border-red-300 bg-red-50' : 'border-neutral-300'
                          }`}
                        />
                        {batch && (
                          <p className="text-xs text-neutral-500 mt-1">
                            Available: {availableQty}
                          </p>
                        )}
                        {isInsufficientStock && (
                          <p className="text-xs text-red-600 mt-1 flex items-center">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Insufficient stock
                          </p>
                        )}
                        {errors.items?.[index]?.qty && (
                          <p className="text-sm text-red-600 mt-1">
                            {errors.items[index]?.qty?.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Unit Price
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          {...register(`items.${index}.unitPrice`, { valueAsNumber: true })}
                          readOnly
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          MRP
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          {...register(`items.${index}.mrp`, { valueAsNumber: true })}
                          readOnly
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50"
                        />
                      </div>
                    </div>

                    {/* Line Total */}
                    <div className="mt-4 text-right">
                      <span className="text-sm text-neutral-600">
                        Line Total: $
                        {((watchedItems[index]?.qty || 0) * (watchedItems[index]?.unitPrice || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Additional Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Payment Status
              </label>
              <select
                {...register('paymentStatus')}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Notes
              </label>
              <textarea
                {...register('notes')}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Summary */}
          <div className="bg-neutral-50 rounded-lg p-4">
            <div className="flex justify-between font-semibold text-lg">
              <span>Total Amount:</span>
              <span>${calculateTotal().toFixed(2)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-neutral-700 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? 'Dispensing...' : 'Dispense Medicines'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
